import React, { useState, useEffect } from "react";
import { FaPlus } from "react-icons/fa";
import { MdClose, MdSave, MdUpdate } from "react-icons/md";
import DietPlanHeader from "./DietPlanHeader";
import DietPlanDay from "./DietPlanDay";
import PatientTracking from "./PatientTracking";

function DietPlanFormModal({ 
  isOpen, 
  onClose, 
  onSave, 
  loading, 
  initialData = null, 
  isEditing = false,
  loginResultObject 
}) {
  const [payload, setPayload] = useState({
    chartTitle: "",
    createDate: new Date().toISOString().split("T")[0],
    noOfDays: 1,
    planStatus: "Active",
    mealConfirmation: 1,
    imageUploadRequired: 1,
    reminderNotification: 1,
    createdBy: loginResultObject?.user?.userId,
    modifiedBy: loginResultObject?.user?.userId,
    accountId: loginResultObject?.user?.account_Id,
    clinicId: loginResultObject?.user?.clinic_Id,
    paymentRequiredYN: "N",
    amount: "",
  });

  const [groupedMeals, setGroupedMeals] = useState({
    "Day 1": [
      { mealTime: "08:30", mealType: "Breakfast", meal: "", activity: "", notes: "" },
      { mealTime: "13:30", mealType: "Lunch", meal: "", activity: "", notes: "" },
      { mealTime: "20:30", mealType: "Dinner", meal: "", activity: "", notes: "" }
    ]
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setPayload({
          chartTitle: initialData.chartTitle,
          createDate: initialData.createDate,
          noOfDays: initialData.noOfDays,
          planStatus: initialData.planStatus,
          mealConfirmation: initialData.mealConfirmation,
          imageUploadRequired: initialData.imageUploadRequired,
          reminderNotification: initialData.reminderNotification,
          createdBy: initialData.createdBy,
          modifiedBy: loginResultObject?.user?.userId,
          accountId: initialData.accountId,
          clinicId: initialData.clinicId,
          paymentRequiredYN: initialData.paymentRequiredYN || "N",
          amount: initialData.amount || "",
        });

        const meals = {};
        [...(initialData?.dietPlanDetails || [])]
          .sort((a, b) => {
            const dayA = parseInt(a.dayTitle.match(/\d+/)?.[0] || 0);
            const dayB = parseInt(b.dayTitle.match(/\d+/)?.[0] || 0);
            if (dayA !== dayB) return dayA - dayB;
            return (a.sortOrder || 0) - (b.sortOrder || 0);
          })
          .forEach((detail) => {
            if (!meals[detail.dayTitle]) meals[detail.dayTitle] = [];
            meals[detail.dayTitle].push({
              ...detail,
              mealTime: detail.mealTime.substring(0, 5),
              mealType: detail.mealType,
              meal: detail.meal,
              activity: detail.activity || "",
              notes: detail.notes || "",
            });
          });
        setGroupedMeals(meals);
      } else {
        setPayload({
          chartTitle: "",
          createDate: new Date().toISOString().split("T")[0],
          noOfDays: 1,
          planStatus: "Active",
          mealConfirmation: 1,
          imageUploadRequired: 1,
          reminderNotification: 1,
          createdBy: loginResultObject?.user?.userId,
          modifiedBy: loginResultObject?.user?.userId,
          accountId: loginResultObject?.user?.account_Id,
          clinicId: loginResultObject?.user?.clinic_Id,
          paymentRequiredYN: "N",
          amount: "",
        });
        setGroupedMeals({
          "Day 1": [
            { mealTime: "08:30", mealType: "Breakfast", meal: "", activity: "", notes: "" },
            { mealTime: "13:30", mealType: "Lunch", meal: "", activity: "", notes: "" },
            { mealTime: "20:30", mealType: "Dinner", meal: "", activity: "", notes: "" }
          ]
        });
      }
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    const currentDays = Object.keys(groupedMeals).length;
    const targetDays = payload.noOfDays || 0;

    if (targetDays > currentDays) {
      const newGroupedMeals = { ...groupedMeals };
      for (let i = currentDays + 1; i <= targetDays; i++) {
        newGroupedMeals[`Day ${i}`] = [
          { mealTime: "08:30", mealType: "Breakfast", meal: "", activity: "", notes: "" },
          { mealTime: "13:30", mealType: "Lunch", meal: "", activity: "", notes: "" },
          { mealTime: "20:30", mealType: "Dinner", meal: "", activity: "", notes: "" }
        ];
      }
      setGroupedMeals(newGroupedMeals);
    } else if (targetDays < currentDays && targetDays >= 1) {
      const newGroupedMeals = {};
      for (let i = 1; i <= targetDays; i++) {
        newGroupedMeals[`Day ${i}`] = groupedMeals[`Day ${i}`];
      }
      setGroupedMeals(newGroupedMeals);
    }
  }, [payload.noOfDays]);

  const handleHeaderChange = (field, value) => {
    setPayload(prev => ({ ...prev, [field]: value }));
  };

  const handleMealChange = (dayTitle, mealIndex, field, value) => {
    setGroupedMeals(prev => {
      const newDayMeals = [...prev[dayTitle]];
      newDayMeals[mealIndex] = { ...newDayMeals[mealIndex], [field]: value };
      return { ...prev, [dayTitle]: newDayMeals };
    });
  };

  const handleActivityChange = (dayTitle, value) => {
    setGroupedMeals(prev => {
      const newDayMeals = prev[dayTitle].map(meal => ({ ...meal, activity: value }));
      return { ...prev, [dayTitle]: newDayMeals };
    });
  };

  const handleNotesChange = (dayTitle, value) => {
    setGroupedMeals(prev => {
      const newDayMeals = prev[dayTitle].map(meal => ({ ...meal, notes: value }));
      return { ...prev, [dayTitle]: newDayMeals };
    });
  };

  const addMeal = (dayTitle) => {
    setGroupedMeals(prev => ({
      ...prev,
      [dayTitle]: [
        ...prev[dayTitle],
        {
          mealTime: "12:00",
          mealType: "",
          meal: "",
          activity: prev[dayTitle][0]?.activity || "",
          notes: prev[dayTitle][0]?.notes || ""
        }
      ]
    }));
  };

  const removeMeal = (dayTitle, mealIndex) => {
    setGroupedMeals(prev => ({
      ...prev,
      [dayTitle]: prev[dayTitle].filter((_, i) => i !== mealIndex)
    }));
  };

  const copyFromPrevious = (dayTitle) => {
    const dayNumber = parseInt(dayTitle.split(" ")[1]);
    if (dayNumber <= 1) return;

    const previousDayTitle = `Day ${dayNumber - 1}`;
    const previousDayMeals = groupedMeals[previousDayTitle];

    if (previousDayMeals) {
      setGroupedMeals(prev => ({
        ...prev,
        [dayTitle]: previousDayMeals.map(meal => ({ ...meal }))
      }));
    }
  };

  const handleSubmit = () => {
    const dietPlanDetails = [];
    Object.keys(groupedMeals).forEach((dayTitle, dayIndex) => {
      groupedMeals[dayTitle].forEach((meal, mealIndex) => {
        dietPlanDetails.push({
          ...meal,
          dayTitle,
          dayStatus: meal.dayStatus || (dayIndex === 0 ? "Active Plan" : "Planned"),
          sortOrder: mealIndex,
          mealTime: meal.mealTime + (meal.mealTime.length === 5 ? ":00" : ""),
          mealType: meal.mealType,
          meal: meal.meal,
          activity: meal.activity || "",
          notes: meal.notes || ""
        });
      });
    });
    onSave({ ...payload, dietPlanDetails });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-[#f8fafc] w-full max-w-6xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-modalOpen border border-white/20">
        <div className="bg-white px-6 py-4 flex justify-between items-center border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-primaryColor/10 rounded-lg flex items-center justify-center">
              {isEditing ? <MdUpdate className="text-brand-primaryColor" /> : <FaPlus className="text-brand-primaryColor" />}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {isEditing ? "Edit Diet Plan" : "Create New Diet Plan"}
              </h2>
              <p className="text-xs text-gray-500 font-medium">Design a personalized nutrition strategy</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-50 hover:text-red-500 rounded-full transition-all text-gray-400 bg-white"
          >
            <MdClose size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollHide">
          <DietPlanHeader
            headerData={payload}
            onChange={handleHeaderChange}
          />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-4 bg-brand-primaryColor rounded-full"></span>
                Meal Schedule
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {Object.keys(groupedMeals).map((dayTitle, index) => (
                <DietPlanDay
                  key={dayTitle}
                  dayTitle={dayTitle}
                  meals={groupedMeals[dayTitle]}
                  onMealChange={(mealIndex, field, value) => handleMealChange(dayTitle, mealIndex, field, value)}
                  onAddMeal={() => addMeal(dayTitle)}
                  onRemoveMeal={(mealIndex) => removeMeal(dayTitle, mealIndex)}
                  onActivityChange={(value) => handleActivityChange(dayTitle, value)}
                  onNotesChange={(value) => handleNotesChange(dayTitle, value)}
                  onCopyPrevious={index > 0 ? () => copyFromPrevious(dayTitle) : null}
                />
              ))}
            </div>
          </div>

          <PatientTracking
            trackingData={payload}
            onChange={handleHeaderChange}
          />
        </div>

        <div className="bg-white px-8 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all border border-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-8 py-2 bg-brand-primaryColor text-white rounded-xl text-sm font-bold shadow-lg shadow-brand-primaryColor/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-70"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                {isEditing ? "Updating..." : "Saving..."}
              </span>
            ) : (
              <>
                {isEditing ? <MdUpdate size={18} /> : <MdSave size={18} />}
                {isEditing ? "Update Diet Plan" : "Save Diet Plan"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DietPlanFormModal;
